// Rule-evaluation engine. Holds the rule registry + dedupe cache.
//
// Lifecycle:
//   - register(rule)                — boot-time wiring
//   - setEnabled(id, on)            — per-rule preference
//   - setRuleParams(id, params)     — per-rule preference (e.g. threshold)
//   - onEvent(event, ctx) → list    — fan a realtime event through enabled
//                                     rules with matching appliesTo
//   - onTick(ctx) → list            — fan a tick through enabled rules
//                                     that implement onTick
//
// The engine itself doesn't fire any speech — it returns a list of
// Instructions filtered through the dedupe gate. The orchestrator hook
// is what actually calls speechSynthesis.
//
// Dedupe model: an Instruction with `dedupeKey = K` fires at most once
// per `dedupeWindowMs` (default DEDUPE_DEFAULT_MS). Rules can pick
// long-lived keys (e.g. "until_day:transition-id-abc") to make a
// once-per-event instruction, or short-lived keys + small windows for
// throttled streams.

import type { RealtimeEvent } from "@/lib/realtime/events";
import type {
  AnnouncementContext,
  AnnouncementRule,
  Instruction,
} from "./types";

export const DEDUPE_DEFAULT_MS = 60_000;

interface RuleState {
  rule: AnnouncementRule;
  enabled: boolean;
  params: Record<string, unknown>;
}

export class RuleEngine {
  private rules: Map<string, RuleState> = new Map();
  private dedupe: Map<string, number> = new Map();

  register(rule: AnnouncementRule): void {
    this.rules.set(rule.id, {
      rule,
      enabled: rule.defaultEnabled,
      params: {},
    });
  }

  setEnabled(id: string, enabled: boolean): void {
    const s = this.rules.get(id);
    if (s) s.enabled = enabled;
  }

  setRuleParams(id: string, params: Record<string, unknown>): void {
    const s = this.rules.get(id);
    if (s) s.params = params;
  }

  /**
   * Snapshot of currently-registered rules and their effective
   * enabled+params state. Used by the settings UI to render toggles.
   */
  listRules(): Array<{
    id: string;
    label: string;
    enabled: boolean;
    params: Record<string, unknown>;
  }> {
    return Array.from(this.rules.values()).map((s) => ({
      id: s.rule.id,
      label: s.rule.label,
      enabled: s.enabled,
      params: s.params,
    }));
  }

  /**
   * Reset the dedupe cache. Useful from the UI when a user toggles a
   * rule back on or changes voice params and wants the next fire to
   * happen immediately rather than wait out a stale gate.
   */
  clearDedupe(): void {
    this.dedupe.clear();
  }

  /**
   * Apply a rule's instruction through the dedupe gate. Returns the
   * instruction if it should be spoken now, or null if it was
   * suppressed (or no instruction was produced).
   */
  private gate(inst: Instruction | null, ctx: AnnouncementContext): Instruction | null {
    if (!inst) return null;
    const last = this.dedupe.get(inst.dedupeKey);
    const window = inst.dedupeWindowMs ?? DEDUPE_DEFAULT_MS;
    if (last !== undefined && ctx.nowMs - last < window) return null;
    this.dedupe.set(inst.dedupeKey, ctx.nowMs);
    return inst;
  }

  onEvent(event: RealtimeEvent, ctxBase: Omit<AnnouncementContext, "ruleParams">): Instruction[] {
    const out: Instruction[] = [];
    for (const s of this.rules.values()) {
      if (!s.enabled) continue;
      if (!s.rule.onEvent) continue;
      if (s.rule.appliesTo && s.rule.appliesTo !== event.type) continue;
      const ctx: AnnouncementContext = { ...ctxBase, ruleParams: s.params };
      const inst = this.gate(s.rule.onEvent(event, ctx), ctx);
      if (inst) out.push(inst);
    }
    return out;
  }

  onTick(ctxBase: Omit<AnnouncementContext, "ruleParams">): Instruction[] {
    const out: Instruction[] = [];
    for (const s of this.rules.values()) {
      if (!s.enabled) continue;
      if (!s.rule.onTick) continue;
      const ctx: AnnouncementContext = { ...ctxBase, ruleParams: s.params };
      const inst = this.gate(s.rule.onTick(ctx), ctx);
      if (inst) out.push(inst);
    }
    return out;
  }
}
